// Shared fulfilment-tab logic for the seller My Orders screen + the sidebar
// count badges. One order maps to exactly one tab; the sidebar and the list
// derive their partition from the same function so counts always match.

export type FulfillmentTab =
  | 'awaiting_payment'
  | 'on_hold'
  | 'awaiting_shipment'
  | 'shipped'
  | 'self_fulfilled'
  | 'cancelled'
  | 'returned'

export const TAB_LABEL: Record<FulfillmentTab, string> = {
  awaiting_payment: 'Awaiting Payment',
  on_hold: 'On Hold',
  awaiting_shipment: 'Awaiting Shipment',
  shipped: 'Shipped',
  self_fulfilled: 'Self-fulfilled',
  cancelled: 'Cancelled',
  returned: 'Returned',
}

/** Badge classes per fulfilment tab — shared so the admin order list/detail can
 * show the real marketplace status (instead of the internal entry state) with
 * the same visual language the seller surface uses. */
export const TAB_BADGE_CLASSES: Record<FulfillmentTab, string> = {
  awaiting_payment: 'bg-amber-50 text-amber-700 ring-amber-200',
  on_hold: 'bg-orange-50 text-orange-700 ring-orange-200',
  awaiting_shipment: 'bg-sky-50 text-sky-700 ring-sky-200',
  shipped: 'bg-emerald-50 text-emerald-700 ring-emerald-200',
  // Self-fulfilled = shipped on the marketplace WITHOUT going through PEXIFY.
  // Slate signals "outside our operation".
  self_fulfilled: 'bg-slate-100 text-slate-600 ring-slate-300',
  cancelled: 'bg-rose-50 text-rose-700 ring-rose-200',
  returned: 'bg-rose-100 text-rose-800 ring-rose-200',
}

/** Internal states that mean PEXIFY shipped the order through its own pipeline. */
const PEXIFY_SHIPPED_STATES = new Set(['shipped', 'delivered', 'settled'])

/** The minimal order shape the tab partition needs. */
export type TabbableOrder = {
  marketplace_status?: string | null
  marketplace_is_shipped?: boolean
  state: string
  hold_until?: string | null
  /** Terminal-return marker (return_credit issuance Q9 sync). NOT a `state`
   * value — the orders_state_valid CHECK has no 'returned' enum member, so
   * this dedicated timestamp is the only reliable return signal. */
  returned_at?: string | null
  /** The order holds a bought, not-voided PEXIFY label — admin lens only (the
   * seller rows carry no label). Backend mirror: services/order.has_active_label. */
  has_active_label?: boolean
}

/** Cancelled or fully refunded on the marketplace. A PARTIAL refund is not
 * terminal: the remaining goods still ship, so the order stays in its natural
 * tab (Awaiting Shipment until shipped). Backend mirrors: seller orders summary
 * + services/order._fulfillment_condition. */
export function isTerminalMarketplaceStatus(status: string): boolean {
  const s = status.toLowerCase()
  return s.includes('cancel') || (s.includes('refund') && !s.includes('partial'))
}

export function fulfillmentTabOf(o: TabbableOrder): FulfillmentTab {
  // On Hold is a deliberate seller action and takes priority over the
  // marketplace status — a held order shows under On Hold whatever its status,
  // and falls back to its natural tab once the hold expires (cron clears it).
  if (o.hold_until && Date.parse(o.hold_until) > Date.now()) {
    return 'on_hold'
  }
  // returned_at is NOT read here: the seller surfaces (SellerShell sidebar +
  // RealMyOrders mobile queues) have no Returned queue to route these orders
  // into, so reclassifying them here would make them vanish from every named
  // seller queue. adminFulfillmentTabOf applies the returned_at override for
  // the admin lens instead, where the "Returned" chip exists to hold them.
  const status = (o.marketplace_status ?? '').toLowerCase()
  if (isTerminalMarketplaceStatus(status) || o.state === 'cancelled') {
    return 'cancelled'
  }
  if (
    status === 'completed' ||
    status === 'shipped' ||
    o.marketplace_is_shipped ||
    o.state === 'shipped' ||
    o.state === 'delivered' ||
    o.state === 'settled'
  ) {
    return 'shipped'
  }
  // A marketplace "payment" status, or PEXIFY's own payment_hold (N-07 / Blok
  // B1: approved, but the wallet did not cover it — approve again once funded).
  if (status.includes('payment') || o.state === 'payment_hold') {
    return 'awaiting_payment'
  }
  return 'awaiting_shipment'
}

/** ADMIN lens over fulfillmentTabOf. The seller view treats every shipped order
 * the same ("I shipped it"); the platform owner needs to tell apart orders WE
 * fulfilled from ones the seller shipped directly on the marketplace, bypassing
 * PEXIFY. An order whose marketplace says shipped but whose internal state never
 * reached a PEXIFY-shipped state was self-fulfilled. (Not used on the seller
 * surface — only the admin order list/detail.)
 *
 * Also applies the returned_at override here (not in the shared
 * fulfillmentTabOf): the admin "Returned" chip exists to hold these orders,
 * the seller surfaces don't, so this stays admin-only. On Hold still wins —
 * `base === 'on_hold'` means hold_until is set, which a deliberate seller
 * action should keep showing regardless of a later return. */
export function adminFulfillmentTabOf(o: TabbableOrder): FulfillmentTab {
  const base = fulfillmentTabOf(o)
  if (base !== 'on_hold' && o.returned_at) {
    return 'returned'
  }
  // Marketplace-shipped with OUR label: PEXIFY shipped it, not the seller.
  if (base === 'shipped' && !PEXIFY_SHIPPED_STATES.has(o.state) && !o.has_active_label) {
    return 'self_fulfilled'
  }
  // A bought label means the parcel is leaving (Volkan 2026-10-29): the order
  // reads as Shipped; voiding the label brings it back to Awaiting Shipment.
  if (base === 'awaiting_shipment' && o.has_active_label) {
    return 'shipped'
  }
  return base
}

export function computeTabCounts(
  orders: TabbableOrder[],
): Record<FulfillmentTab, number> {
  const c: Record<FulfillmentTab, number> = {
    awaiting_payment: 0,
    on_hold: 0,
    awaiting_shipment: 0,
    shipped: 0,
    self_fulfilled: 0,
    cancelled: 0,
    returned: 0,
  }
  for (const o of orders) c[fulfillmentTabOf(o)] += 1
  return c
}

/** Server-computed whole-org tab counts (GET /seller/orders/summary). The
 * backend partitions with the same bucket/priority rules as fulfillmentTabOf,
 * but over EVERY order — not just the rows one list page can carry. */
export type SellerOrderTabSummary = {
  total: number
  tabs: Record<FulfillmentTab, number>
  /** Awaiting Shipment by shop; sums to tabs.awaiting_shipment. */
  awaiting_shipment_by_store?: {
    store_integration_id: string | null
    store_origin: string
    count: number
  }[]
}

/** Server-computed My Orders Store/Assignee filter menu sources (GET
 * /seller/orders/filter-options) — every shop key and assignee name present
 * anywhere in the org, whole-org, without paging through every order. */
export type SellerOrderFilterOptions = {
  stores: { store_integration_id: string | null; store_origin: string }[]
  assignee_names: string[]
}
